import * as React from 'react';
import * as _ from 'lodash';
import { Dispatch, connect } from 'react-redux';
import {
    IApplicationState, AppActionCreators,
    DashActionCreators, ILookupDataResponse, DonorSearchActionCreators,
    IDashSelfServeReconciliationCreateResponse,
    IDashSelfServeReconciliationUploadResponse,
    IDashSelfServeReconciliationProcessResponse
} from '@nkr/store/index';
import { IBaseResponse, IInvalidRow, IListItem } from '@nkr/models/index';
import { IExtendedReconciliationItem, IRequiredField, ReconciliationItemStatus } from './models';
import { Alert, Button, Well } from 'react-bootstrap';
import { mockData } from './mock-data';
import { toast } from 'react-toastify';
import Upload from './upload';
import PendingItems from './pending-items';
import CompletedItems from './completed-items';
import InvalidRows from './invalid-rows';
import ColumnMapping from './column-mapping';

interface IOwnProps {
    centerId: number;
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getLookupData: (centerId: number) => Promise<ILookupDataResponse>,
    uploadDashSelfServeReconciliationFile: (centerId: number, hasColumnHeaders: boolean, file: File) => Promise<IDashSelfServeReconciliationUploadResponse>,
    processDashSelfServeReconciliationFile: (centerId: number, hasColumnHeaders: boolean, mappedFields: string[], fileName: string) => Promise<IDashSelfServeReconciliationProcessResponse>,
    updateSelfServeReconciliationEntry: (
        id: number, firstName: string,
        lastName: string,
        birthDate: string,
        donationType: number,
        transplantDate: string) => Promise<IBaseResponse>,
    createSelfServeReconciliationEntry: (
        centerId: number,
        firstName: string,
        lastName: string,
        birthDate: string,
        donationType: number,
        transplantDate: string
    ) => Promise<IDashSelfServeReconciliationCreateResponse>
}

type TProps = IStateProps & IDispatchProps & IOwnProps;

interface IState {
    reconciliationItems: IExtendedReconciliationItem[],
    invalidRows: IInvalidRow[],
    closedLoopManualConfirmTypes: IListItem[],
    fileWasUploaded: boolean,
    fileWasProcessed: boolean,
    headers: string[],
    sampleRow: string[],
    requiredFields: IRequiredField[],
    originalFileName: string,
    fileName: string,
    mappedFields: string[],
    mappedFieldsValid: boolean,
    hasColumnHeaders: boolean,
    isDirty: boolean
}

const initialState: IState = {
    reconciliationItems: __ENVIRONMENT__ == 'dev2' ? mockData.items.map((item, index) => {
        return {
            ...item,
            index,
            status: ReconciliationItemStatus.Pending,
            newEntry: false,
            matches: item.matches.map(m => {
                return { ...m, selected: false };
            })
        };
    }) : [],
    invalidRows: __ENVIRONMENT__ == 'dev2' ? mockData.invalidRows : [],
    closedLoopManualConfirmTypes: [],
    fileWasUploaded: false, //__ENVIRONMENT__ == 'dev' ? true : false,
    fileWasProcessed: false,
    headers: [],
    sampleRow: [],
    requiredFields: [],
    originalFileName: '',
    fileName: '',
    mappedFields: ['', '', '', '', ''],
    mappedFieldsValid: true,
    hasColumnHeaders: false,
    isDirty: false
};

class Reconciliation extends React.Component<TProps, IState> {
    readonly state = initialState;

    componentDidMount = () => {
        this.getData();
    };

    getData = () => {
        this.props.showLoading('Turning knobs, one moment please...').then(() => {
            this.props.getLookupData(this.props.centerId).then((response) => {
                if (response.data.success) {
                    let { closedLoopManualConfirmTypes } = response.data.results;

                    this.setState({
                        closedLoopManualConfirmTypes,
                    });
                }

                this.props.hideLoading();
            });
        });
    };

    getResetButtonLabel = (pendingItems: IExtendedReconciliationItem[], completedItems: IExtendedReconciliationItem[]) => {
        if (pendingItems.length == 0 && completedItems.length == 0) {
            return <>Discard &amp; Start Over</>;
        }

        if (pendingItems.length > 0 && completedItems.length == 0) {
            return <>Discard Pending Items &amp; Start Over</>;
        }

        if (pendingItems.length > 0 && completedItems.length > 0) {
            return <>Discard Remaining Pending Items &amp; Start Over</>;
        }

        if (pendingItems.length == 0 && completedItems.length > 0) {
            return <>Upload Another File</>;
        }
    }

    handleFileUpload = (hasColumnHeaders: boolean, file: File) => {
        this.props.showLoading('Uploading & analyzing file, one moment please...').then(() => {
            this.props.uploadDashSelfServeReconciliationFile(this.props.centerId, hasColumnHeaders, file)
                .then((response) => {
                    if (response.data.success) {
                        let { headers, sampleRow, requiredFields, fileName } = response.data.results;

                        this.setState({
                            headers,
                            sampleRow,
                            requiredFields,
                            mappedFields: hasColumnHeaders ? requiredFields.map(f => f.fieldFromFile) : ['', '', '', '', ''],
                            originalFileName: file.name,
                            fileName,
                            fileWasUploaded: true,
                            fileWasProcessed: false,
                            hasColumnHeaders
                        });
                    }

                    this.props.hideLoading();
                })
                .catch(error => {
                    this.props.hideLoading();
                });
        });
    };

    handleProcessFile = () => {
        let { hasColumnHeaders, mappedFields, fileName } = this.state;

        let mappedFieldsValid = mappedFields.filter(field => field == '').length == 0;

        this.setState({
            mappedFieldsValid,
            isDirty: true
        }, () => {
            if (this.state.mappedFieldsValid) {
                this.props.showLoading('Processing file, one moment please...').then(() => {
                    this.props.processDashSelfServeReconciliationFile(this.props.centerId, hasColumnHeaders, mappedFields, fileName)
                        .then(response => {
                            let { items, invalidRows } = response.data.results;

                            this.setState({
                                reconciliationItems: items.map((item, index) => {
                                    return {
                                        ...item,
                                        index,
                                        status: ReconciliationItemStatus.Pending,
                                        newEntry: false,
                                        matches: item.matches.map(m => {
                                            return { ...m, selected: false };
                                        })
                                    };
                                }),
                                invalidRows,
                                fileWasProcessed: true
                            });

                            this.props.hideLoading();
                        })
                        .catch(error => {
                            this.props.hideLoading();
                        });
                });
            }
        });
    }

    handleShowHideUpdate = (itemIndex: number, registrationId: number, show: boolean) => {
        let reconciliationItems = _.cloneDeep(
            this.state.reconciliationItems.map(item => {
                if (item.index == itemIndex) {
                    let match = item.matches.find(x => x.id == registrationId);
                    match.selected = show;
                }

                return item;
            })
        );

        this.setState({
            reconciliationItems
        });
    };

    handleShowHideNewEntry = (itemIndex: number, show: boolean) => {
        let reconciliationItems = _.cloneDeep(
            this.state.reconciliationItems.map(item => {
                if (item.index == itemIndex) {
                    item.newEntry = show;
                }

                return item;
            })
        );

        this.setState({
            reconciliationItems
        });
    };

    handleUpdate = (itemIndex: number, matchId: number, firstName: string, lastName: string, birthDate: string, transplantDate: string, donationType: number) => {
        this.props.showLoading('Updating DASH entry, one moment please...').then(() => {
            this.props.updateSelfServeReconciliationEntry(matchId, firstName, lastName, birthDate, donationType, transplantDate).then(response => {
                if (response.data.success) {
                    let reconciliationItems = this.state.reconciliationItems.map(item => {
                        if (item.index == itemIndex) {
                            item.status = ReconciliationItemStatus.CompleteExisting;
                        }

                        return { ...item };
                    });

                    this.setState({
                        reconciliationItems
                    }, () => {
                        toast.success('DASH entry updated successfully');
                    });
                }

                this.props.hideLoading();
            });
        });
    };

    handleCreateNewEntry = (itemIndex: number, firstName: string, lastName: string, birthDate: string, transplantDate: string, donationType: number) => {
        this.props.showLoading('Creating new DASH entry, one moment please...').then(() => {
            this.props.createSelfServeReconciliationEntry(this.props.centerId, firstName, lastName, birthDate, donationType, transplantDate).then(response => {
                if (response.data.success) {
                    let reconciliationItems = this.state.reconciliationItems.map(item => {
                        if (item.index == itemIndex) {
                            item.status = ReconciliationItemStatus.CompleteNew;
                        }

                        return { ...item };
                    });

                    this.setState({
                        reconciliationItems
                    }, () => {
                        toast.success('DASH entry created successfully');
                    });
                }

                this.props.hideLoading();
            });
        });
    };

    handleMappingChange = (mappedFields: string[]) => {
        this.setState({
            mappedFields,
            mappedFieldsValid: mappedFields.filter(field => field == '').length == 0
        });
    }

    handleReset = () => {
        let closedLoopManualConfirmTypes = _.cloneDeep(this.state.closedLoopManualConfirmTypes)

        let newState = { ...initialState, closedLoopManualConfirmTypes };

        this.setState(newState);
    }

    handleColumnMapping = () => {
        this.setState({
            fileWasProcessed: false,
            invalidRows: []
        });
    }

    render = () => {
        const { reconciliationItems, invalidRows } = this.state;
        const pendingItems = reconciliationItems.filter(x => x.status == ReconciliationItemStatus.Pending);
        const completedItems = reconciliationItems.filter(x => x.status != ReconciliationItemStatus.Pending);

        return (
            <div className='container' style={{ paddingBottom: 10 }}>
                {this.state.isDirty && this.state.mappedFieldsValid == false && (
                    <Alert bsStyle='danger'>
                        All required columns need to be mapped to corresponding columns in the file.
                        <br /><br />
                        The fields in red are required.
                    </Alert>
                )}

                <Well>
                    <h4 style={{ marginTop: 0, marginBottom: 15 }}>DASH SELF-SERVE RECONCILIATION</h4>
                    <div>This page will allow you to reconcile UNOS donors with DASH records. To begin this process, upload a comma separated file (CSV) of UNOS donors.</div>
                </Well>

                {this.state.fileWasUploaded == false && this.state.fileWasProcessed == false && (
                    <Upload onFileUpload={this.handleFileUpload} />
                )}

                {this.state.originalFileName != '' && (
                    <Alert bsStyle='info'>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>{this.state.originalFileName} was uploaded...</div>
                            <div>
                                <Button onClick={this.handleReset}>{this.getResetButtonLabel(pendingItems, completedItems)}</Button>
                            </div>
                        </div>
                    </Alert>
                )}

                {this.state.fileWasUploaded && this.state.fileWasProcessed == false && (
                    <>
                        <ColumnMapping
                            headers={this.state.headers}
                            sampleRow={this.state.sampleRow}
                            requiredFields={this.state.requiredFields}
                            mappedFields={this.state.mappedFields}
                            hasColumnHeaders={this.state.hasColumnHeaders}
                            isDirty={this.state.isDirty}
                            onMappingChange={this.handleMappingChange}
                        />

                        <div className='text-center'>
                            <Button bsStyle='primary' onClick={this.handleProcessFile}>Process Uploaded File</Button>
                        </div>
                    </>
                )}

                {this.state.fileWasUploaded && this.state.fileWasProcessed && (
                    <PendingItems
                        data={pendingItems}
                        closedLoopManualConfirmTypes={this.state.closedLoopManualConfirmTypes}
                        onShowHideUpdate={this.handleShowHideUpdate}
                        onUpdate={this.handleUpdate}
                        onShowHideNewEntry={this.handleShowHideNewEntry}
                        onCreateNewEntry={this.handleCreateNewEntry}
                    />
                )}

                {completedItems.length > 0 && (
                    <CompletedItems
                        data={completedItems}
                    />
                )}

                {invalidRows.length > 0 && (
                    <InvalidRows
                        data={invalidRows}
                        onChangeColumnMapping={this.handleColumnMapping}
                    />
                )}
            </div>
        );
    };
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getLookupData: (centerId) => {
            return dispatch(DonorSearchActionCreators.getLookupData(centerId));
        },
        uploadDashSelfServeReconciliationFile: (centerId, hasColumnHeaders, file) => {
            return dispatch(DashActionCreators.uploadDashSelfServeReconciliationFile(centerId, hasColumnHeaders, file));
        },
        processDashSelfServeReconciliationFile: (centerId, hasColumnHeaders, mappedFields, fileName) => {
            return dispatch(DashActionCreators.processDashSelfServeReconciliationFile(centerId, hasColumnHeaders, mappedFields, fileName));
        },
        updateSelfServeReconciliationEntry: (id, firstName, lastName, birthDate, donationType, transplantDate) => {
            return dispatch(DashActionCreators.updateSelfServeReconciliationEntry(id, firstName, lastName, birthDate, donationType, transplantDate));
        },
        createSelfServeReconciliationEntry: (centerId, firstName, lastName, birthDate, donationType, transplantDate) => {
            return dispatch(DashActionCreators.createSelfServeReconciliationEntry(centerId, firstName, lastName, birthDate, donationType, transplantDate));
        }
    };
};

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    null,
    mapDispatchToProps)(Reconciliation);
